import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  NotificationsActive,
  Save,
} from '@mui/icons-material';
import { alertsApi } from '../api';
import { useLiveMqttValues } from '../hooks/useLiveMqttValues';
import { bcp47, useI18n } from '../i18n';
import { DataTable, DataTableColumn } from '../table';
import { openFaults } from './openFaults';
import type { OpenFault } from './openFaults';
import {
  AlertEvent,
  TemperatureAlertRule,
} from './types';

function withCriticality(rule: TemperatureAlertRule): TemperatureAlertRule {
  const n = Number(rule.criticality);
  return { ...rule, criticality: n === 1 || n === 3 ? n : 2 };
}

function readText(value: { statusRaw?: string | null; raw?: string } | null | undefined): string {
  if (!value) {
    return '';
  }
  if (typeof value.statusRaw === 'string' && value.statusRaw.trim()) {
    return value.statusRaw.trim();
  }
  return '';
}

function readTemp(value: { temp?: number | null; value?: number | null } | null | undefined): number | null {
  if (!value) {
    return null;
  }
  if (typeof value.temp === 'number' && Number.isFinite(value.temp)) {
    return value.temp;
  }
  if (typeof value.value === 'number' && Number.isFinite(value.value)) {
    return value.value;
  }
  return null;
}

const AlertingPage: React.FC = () => {
  const { t } = useI18n();
  const [rules, setRules] = useState<TemperatureAlertRule[]>([]);
  const [savedRules, setSavedRules] = useState<TemperatureAlertRule[]>([]);
  const [flowRules, setFlowRules] = useState<TemperatureAlertRule[]>([]);
  const [events, setEvents] = useState<AlertEvent[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [ackingId, setAckingId] = useState<string | null>(null);

  const topics = useMemo(
    () => [...rules, ...flowRules].map((rule) => rule.topic).filter(Boolean),
    [rules, flowRules],
  );
  const { values, mqttConfigured } = useLiveMqttValues(topics);
  const dirty = JSON.stringify(rules) !== JSON.stringify(savedRules);
  const faults = useMemo(() => openFaults(events), [events]);

  const applyDoc = (doc: {
    rules?: TemperatureAlertRule[];
    flowRules?: TemperatureAlertRule[];
    events?: AlertEvent[];
  }) => {
    const manual = (doc.rules || []).map(withCriticality);
    setRules(manual);
    setSavedRules(manual);
    setFlowRules((doc.flowRules || []).map(withCriticality));
    setEvents(doc.events || []);
  };

  const load = useCallback(async () => {
    try {
      setError(null);
      applyDoc(await alertsApi.get());
    } catch (err) {
      console.error(err);
      setError(t('alerting.loadFailed'));
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const tick = () => {
      alertsApi.get().then((doc) => {
        const next = doc.events || [];
        setEvents((prev) => {
          const prevMax = prev.reduce((max, event) => Math.max(max, event.at), 0);
          const nextMax = next.reduce((max, event) => Math.max(max, event.at), 0);
          return nextMax < prevMax ? prev : next;
        });
        setFlowRules((doc.flowRules || []).map(withCriticality));
      }).catch(() => undefined);
    };
    const timer = setInterval(tick, 5000);
    return () => clearInterval(timer);
  }, []);

  const handleSave = async () => {
    try {
      setBusy(true);
      setError(null);
      const storedRule = (rule: TemperatureAlertRule) => {
        if (!rule.topic.trim()) {
          return false;
        }
        if ((rule.match || '').trim()) {
          return true;
        }
        return rule.min != null || rule.max != null;
      };
      const pending = rules.filter((rule) => !storedRule(rule));
      const savedDoc = await alertsApi.save({
        rules: rules.filter(storedRule),
      });
      applyDoc({ ...savedDoc, flowRules });
      if (pending.length) {
        setRules((prev) => prev.concat(pending));
      }
      setStatus(t('alerting.saved'));
    } catch (err) {
      console.error(err);
      setError(t('alerting.saveFailed'));
    } finally {
      setBusy(false);
    }
  };

  const handleAck = useCallback(async (ruleId: string) => {
    try {
      setAckingId(ruleId);
      setError(null);
      const result = await alertsApi.ack(ruleId);
      setEvents(result.events);
    } catch (err: unknown) {
      console.error(err);
      const message = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      if (message === 'already_acknowledged') {
        setError(t('alerting.alreadyAcknowledged'));
      } else if (message === 'not_open') {
        setError(t('alerting.notOpen'));
      } else {
        setError(t('alerting.ackFailed'));
      }
    } finally {
      setAckingId(null);
    }
  }, [t]);

  const localeTag = bcp47();
  const sideLabel = useCallback((side: AlertEvent['side']) => {
    if (side === 'high') return t('alerting.eventHigh');
    if (side === 'low') return t('alerting.eventLow');
    if (side === 'on') return t('alerting.eventOn');
    if (side === 'off') return t('alerting.eventOff');
    return t('alerting.eventOk');
  }, [t]);

  const sideChip = useCallback((side: AlertEvent['side'], fault = false) => (
    <Chip
      size="small"
      color={
        side === 'high' ? 'error'
          : side === 'low' || side === 'off' || (fault && side === 'on') ? 'warning'
            : 'success'
      }
      label={sideLabel(side)}
    />
  ), [sideLabel]);

  const openColumns = useMemo((): DataTableColumn<OpenFault>[] => [
    {
      id: 'at',
      label: t('alerting.eventTime'),
      accessor: (event) => new Date(event.at).toLocaleString(localeTag),
      sortValue: (event) => event.at,
      render: (event) => (
        <Box sx={{ whiteSpace: 'nowrap' }}>{new Date(event.at).toLocaleString(localeTag)}</Box>
      ),
    },
    {
      id: 'source',
      label: t('alerting.eventSource'),
      accessor: (event) => event.title || event.topic,
    },
    {
      id: 'category',
      label: t('alerting.category'),
      accessor: (event) => (event.criticality == null ? '' : String(event.criticality)),
      sortValue: (event) => event.criticality ?? 0,
      filter: 'select',
      filterOptions: [
        { value: '1', label: '1' },
        { value: '2', label: '2' },
        { value: '3', label: '3' },
      ],
      align: 'right',
    },
    {
      id: 'side',
      label: t('alerting.eventSide'),
      accessor: (event) => sideLabel(event.side),
      filter: 'select',
      filterOptions: [
        { value: t('alerting.eventHigh'), label: t('alerting.eventHigh') },
        { value: t('alerting.eventLow'), label: t('alerting.eventLow') },
        { value: t('alerting.eventOn'), label: t('alerting.eventOn') },
      ],
      render: (event) => sideChip(event.side, true),
    },
    {
      id: 'temp',
      label: t('alerting.eventValue'),
      accessor: (event) => (
        event.temp != null ? `${event.temp.toFixed(1)}` : (event.text || event.message || '')
      ),
      sortValue: (event) => event.temp ?? event.text ?? '',
      align: 'right',
    },
    {
      id: 'message',
      label: t('alerting.eventMessage'),
      accessor: (event) => event.message,
    },
    {
      id: 'ack',
      label: t('alerting.acknowledge'),
      filter: 'none',
      sortable: false,
      stopRowClick: true,
      accessor: (event) => (event.acknowledged ? t('alerting.acknowledged') : ''),
      render: (event) => (event.acknowledged ? (
        <Chip size="small" color="success" label={t('alerting.acknowledged')} />
      ) : (
        <Button
          size="small"
          variant="outlined"
          disabled={ackingId === event.ruleId}
          onClick={() => handleAck(event.ruleId)}
        >
          {t('alerting.acknowledge')}
        </Button>
      )),
    },
  ], [ackingId, handleAck, localeTag, sideChip, sideLabel, t]);

  const eventColumns = useMemo((): DataTableColumn<AlertEvent>[] => {
    return [
      {
        id: 'at',
        label: t('alerting.eventTime'),
        accessor: (event) => new Date(event.at).toLocaleString(localeTag),
        sortValue: (event) => event.at,
        render: (event) => (
          <Box sx={{ whiteSpace: 'nowrap' }}>{new Date(event.at).toLocaleString(localeTag)}</Box>
        ),
      },
      {
        id: 'kind',
        label: t('alerting.eventKind'),
        accessor: (event) => (event.kind === 'ack' ? t('alerting.kindAck') : t('alerting.kindEvent')),
        filter: 'select',
        filterOptions: [
          { value: t('alerting.kindEvent'), label: t('alerting.kindEvent') },
          { value: t('alerting.kindAck'), label: t('alerting.kindAck') },
        ],
        render: (event) => (
          <Chip
            size="small"
            variant={event.kind === 'ack' ? 'filled' : 'outlined'}
            label={event.kind === 'ack' ? t('alerting.kindAck') : t('alerting.kindEvent')}
          />
        ),
      },
      {
        id: 'source',
        label: t('alerting.eventSource'),
        accessor: (event) => event.title || event.topic,
      },
      {
        id: 'side',
        label: t('alerting.eventSide'),
        accessor: (event) => sideLabel(event.side),
        filter: 'select',
        filterOptions: [
          { value: t('alerting.eventHigh'), label: t('alerting.eventHigh') },
          { value: t('alerting.eventLow'), label: t('alerting.eventLow') },
          { value: t('alerting.eventOk'), label: t('alerting.eventOk') },
          { value: t('alerting.eventOn'), label: t('alerting.eventOn') },
          { value: t('alerting.eventOff'), label: t('alerting.eventOff') },
        ],
        render: (event) => sideChip(event.side),
      },
      {
        id: 'criticality',
        label: t('alerting.eventCriticality'),
        accessor: (event) => (event.criticality == null ? '' : String(event.criticality)),
        sortValue: (event) => event.criticality ?? 0,
        filter: 'select',
        filterOptions: [
          { value: '1', label: '1' },
          { value: '2', label: '2' },
          { value: '3', label: '3' },
        ],
        align: 'right',
      },
      {
        id: 'temp',
        label: t('alerting.eventValue'),
        accessor: (event) => (
          event.temp != null ? `${event.temp.toFixed(1)}` : (event.text || event.message || '')
        ),
        sortValue: (event) => event.temp ?? event.text ?? '',
        align: 'right',
      },
      {
        id: 'message',
        label: t('alerting.eventMessage'),
        accessor: (event) => event.message,
      },
    ];
  }, [localeTag, sideChip, sideLabel, t]);

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} mb={1} flexWrap="wrap">
        <Box display="flex" alignItems="center" gap={1}>
          <NotificationsActive color="primary" />
          <Typography variant="h5">{t('alerting.title')}</Typography>
        </Box>
        <Button variant="contained" startIcon={<Save />} onClick={handleSave} disabled={busy || !dirty}>
          {t('common.save')}
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('alerting.subtitle')}
      </Typography>

      {!mqttConfigured && (
        <Alert severity="info" sx={{ mb: 2 }}>{t('alerting.mqttMissing')}</Alert>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {status && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setStatus(null)}>{status}</Alert>}
      {dirty && (
        <Typography variant="caption" color="warning.main" display="block" sx={{ mb: 1 }}>
          {t('common.unsaved')}
        </Typography>
      )}

      <Typography variant="subtitle1" sx={{ mb: 1 }}>{t('alerting.openFaults')}</Typography>
      <Box sx={{ mb: 3 }}>
        <DataTable
          rows={faults}
          columns={openColumns}
          getRowId={(event) => event.ruleId}
          emptyMessage={t('alerting.openFaultsEmpty')}
          defaultSort={{ id: 'at', dir: 'desc' }}
          maxHeight={360}
        />
      </Box>

      <Typography variant="subtitle1" sx={{ mb: 1 }}>{t('alerting.events')}</Typography>
      <Box sx={{ mb: 2 }}>
        <DataTable
          rows={events}
          columns={eventColumns}
          getRowId={(event) => event.id}
          emptyMessage={t('alerting.eventsEmpty')}
          defaultSort={{ id: 'at', dir: 'desc' }}
          maxHeight={420}
        />
      </Box>

      <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} sx={{ mt: 1, mb: 0.5 }}>
        <Typography variant="subtitle1">{t('alerting.stateRules')}</Typography>
        <Button
          size="small"
          variant="outlined"
          onClick={() => setRules((prev) => prev.concat({
            id: `state_${Date.now()}`,
            enable: true,
            topic: '',
            title: '',
            match: '',
            field: '',
            edge: 'both',
            min: null,
            max: null,
            hysteresis: 0,
            alarmDelayMin: 0,
            resetDelayMin: 0,
            criticality: 2,
            channels: ['pushover', 'mail'],
            source: 'manual',
          }))}
        >
          {t('alerting.addState')}
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {t('alerting.stateRulesHint')}
      </Typography>
      {rules.length === 0 && (
        <Typography color="text.secondary" sx={{ mb: 2 }}>{t('alerting.stateRulesEmpty')}</Typography>
      )}
      {rules.map((rule) => {
        const live = readText(values[rule.topic]);
        const patch = (next: Partial<TemperatureAlertRule>) => {
          setRules((prev) => prev.map((item) => (item.id === rule.id ? { ...item, ...next } : item)));
        };
        return (
          <Paper key={rule.id} variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
              <FormControlLabel
                control={(
                  <Switch
                    checked={rule.enable !== false}
                    onChange={(e) => patch({ enable: e.target.checked })}
                  />
                )}
                label={t('alerting.enable')}
              />
              <TextField
                size="small"
                label={t('flow.widgetTitle')}
                value={rule.title}
                onChange={(e) => patch({ title: e.target.value })}
                sx={{ minWidth: 180, flex: 1 }}
              />
              <TextField
                size="small"
                label="Topic"
                value={rule.topic}
                onChange={(e) => patch({ topic: e.target.value })}
                sx={{ minWidth: 220, flex: 1 }}
              />
              <TextField
                size="small"
                label={t('alerting.match')}
                value={rule.match || ''}
                onChange={(e) => patch({ match: e.target.value })}
                sx={{ minWidth: 160, flex: 1 }}
              />
              <TextField
                size="small"
                label={t('alerting.field')}
                value={rule.field || ''}
                onChange={(e) => patch({ field: e.target.value })}
                sx={{ minWidth: 140 }}
              />
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>{t('alerting.edge')}</InputLabel>
                <Select
                  label={t('alerting.edge')}
                  value={rule.edge === 'enter' || rule.edge === 'leave' ? rule.edge : 'both'}
                  onChange={(e) => patch({ edge: e.target.value as TemperatureAlertRule['edge'] })}
                >
                  <MenuItem value="both">{t('alerting.edgeBoth')}</MenuItem>
                  <MenuItem value="enter">{t('alerting.edgeEnter')}</MenuItem>
                  <MenuItem value="leave">{t('alerting.edgeLeave')}</MenuItem>
                </Select>
              </FormControl>
              <TextField
                size="small"
                type="number"
                label={t('alerting.criticality')}
                value={rule.criticality}
                onChange={(e) => {
                  const n = Number(e.target.value);
                  patch({ criticality: n === 1 || n === 3 ? n : 2 });
                }}
                sx={{ width: 110 }}
              />
              <Typography variant="body2" sx={{ minWidth: 88 }}>
                {live || t('alerting.noValue')}
              </Typography>
              <Button size="small" onClick={() => setRules((prev) => prev.filter((item) => item.id !== rule.id))}>
                {t('alerting.removeRule')}
              </Button>
            </Box>
          </Paper>
        );
      })}

      <Typography variant="subtitle1" sx={{ mt: 1, mb: 0.5 }}>{t('alerting.flowRules')}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {t('alerting.flowRulesHint')}
      </Typography>
      {flowRules.length === 0 && (
        <Typography color="text.secondary" sx={{ mb: 2 }}>{t('alerting.flowRulesEmpty')}</Typography>
      )}
      {flowRules.map((rule) => {
        const temp = readTemp(values[rule.topic]);
        const liveText = readText(values[rule.topic]);
        const channelLabel = (rule.channels || []).length
          ? rule.channels!.join(', ')
          : 'pushover, mail';
        return (
          <Paper key={rule.id} variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'action.hover' }}>
            <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
              <Typography variant="body2" sx={{ minWidth: 200, flex: 1 }}>
                {rule.title || rule.topic}
              </Typography>
              <Typography variant="caption" color="text.secondary">{rule.topic}</Typography>
              <Typography variant="body2">
                {(rule.match || '').trim()
                  ? `${t('alerting.match')} ${rule.match}`
                  : `${rule.min != null ? `min ${rule.min}` : '—'} / ${rule.max != null ? `max ${rule.max}` : '—'}`}
              </Typography>
              <Typography variant="caption">{channelLabel}</Typography>
              <Typography variant="body2" sx={{ minWidth: 88 }}>
                {(rule.match || '').trim()
                  ? (liveText || t('alerting.noValue'))
                  : (temp != null ? `${temp.toFixed(1)}` : t('alerting.noValue'))}
              </Typography>
            </Box>
          </Paper>
        );
      })}
    </Container>
  );
};

export default AlertingPage;
